import { Icon } from '@eleo-rh/core-ui'
import cx from 'classnames'
import Button, { type ButtonProps } from 'components/UI/CoreUIButton'
import { useI18n } from 'context/I18nProvider'
import { useBlob } from 'hooks/binary'
import { type MouseEvent, type ReactNode, useState } from 'react'

interface BinaryDownloadLinkProps extends Omit<ButtonProps, 'children' | 'href' | 'target' | 'variant'> {
  href: string
  callback?: (event: MouseEvent<HTMLElement>) => void
  target?: string
  children?: ReactNode
  hidePending?: boolean
  headers?: Record<string, string> | null
  buttonType?: ButtonProps['variant']
  icon?: ReactNode
}

function BinaryDownloadLink({
  href,
  callback,
  target = '',
  children,
  hidePending = false,
  headers = null,
  buttonType = 'primary',
  icon = null,
  ...props
}: BinaryDownloadLinkProps) {
  const { downloadBlob } = useBlob()
  const { i18n } = useI18n()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string>()

  const fetchResource = (e: MouseEvent<HTMLElement>) => {
    setPending(true)
    e.preventDefault()
    e.stopPropagation()
    downloadBlob(href, target, headers)
      .then(() => {
        if (callback) callback(e)
      })
      .catch(err => setError(err.response?.statusText))
      .finally(() => setPending(false))
  }

  return (
    <Button
      variant={buttonType}
      onClick={fetchResource}
      {...props}
      className={cx(props.className, pending && !hidePending && 'text-orange-600!')}
      disabled={pending && !hidePending}
      loading={pending && !hidePending}
    >
      <span className="flex items-center flex-1 gap-2">
        {(error || icon) && (
          <span className="inline-flex items-center">
            {error ? <Icon name="arrows-rotate" color="var(--color-red-500)" /> : icon}
          </span>
        )}
        <span className={cx({ 'text-xs': error || (pending && !hidePending), 'text-red-500': error && !pending })}>
          {children}
        </span>
        {pending && !hidePending && <span>{i18n('app.download.pending')}</span>}
        {!!error && !pending && <span className="text-red-500">{error}</span>}
      </span>
    </Button>
  )
}

export default BinaryDownloadLink
